import { defineStore } from "pinia";
import { ref, computed } from "vue";
import {
  getSession,
  setSession as persistSession,
  clearSession,
  ensureValidSession,
  isUserAdmin,
  isUserDeveloper,
  isUserAiDescribeEnabled,
  isUserAiPhotosEnabled,
  isCurrentUserAiDescribeEnabled,
  isCurrentUserAdmin,
  isCurrentUserDeveloper,
  parseJwtClaimsForDisplay,
  initiateGoogleOAuth,
  initiateNativeGoogleSignIn,
  type AuthSession,
  type AuthUser,
  type ParsedJwtClaims,
} from "@/services/authStore";
import {
  getFeatureFlag,
  setFeatureFlag,
  getAllFeatureFlags,
  listFeatureFlagDefinitions,
  getFeatureFlagDefinition,
  FEATURE_FLAG_AI_DESCRIBE,
  FEATURE_FLAG_AI_PHOTOS,
  FEATURE_FLAG_INTERVALS_ICU_SYNC,
  type FeatureFlagDefinition,
} from "@/services/featureFlags";

export {
  isCurrentUserAiDescribeEnabled,
  isCurrentUserAdmin,
  isCurrentUserDeveloper,
  parseJwtClaimsForDisplay,
};
export {
  getFeatureFlag,
  setFeatureFlag,
  getAllFeatureFlags,
  listFeatureFlagDefinitions,
  getFeatureFlagDefinition,
  FEATURE_FLAG_AI_DESCRIBE,
  FEATURE_FLAG_AI_PHOTOS,
  FEATURE_FLAG_INTERVALS_ICU_SYNC,
};
export type { FeatureFlagDefinition };

export type { AuthSession, AuthUser, ParsedJwtClaims };

export const useAuthSessionStore = defineStore("authSession", () => {
  const session = ref<AuthSession | null>(null);
  const isLoading = ref(false);

  const user = computed(() => session.value?.user ?? null);
  const isAuthenticated = computed(() => session.value !== null);
  const isAdmin = computed(() => session.value ? isUserAdmin(session.value) : false);
  const isDeveloper = computed(() => session.value ? isUserDeveloper(session.value) : false);
  const isAiDescribeEnabled = computed(() =>
    session.value ? isUserAiDescribeEnabled(session.value) : false,
  );
  const isAiPhotosEnabled = computed(() =>
    session.value ? isUserAiPhotosEnabled(session.value) : false,
  );

  async function loadSession(): Promise<AuthSession | null> {
    session.value = await getSession();
    return session.value;
  }

  async function ensureSession(): Promise<AuthSession | null> {
    isLoading.value = true;
    try {
      const valid = await ensureValidSession();
      session.value = valid;
      return valid;
    } finally {
      isLoading.value = false;
    }
  }

  async function signOut(): Promise<void> {
    await clearSession();
    session.value = null;
    if (typeof window !== "undefined") {
      window.location.reload();
    }
  }

  async function setAuthSession(
    nextSession: Pick<AuthSession, "token" | "refreshToken" | "user"> & Partial<Pick<AuthSession, "createdAt">>,
  ): Promise<AuthSession> {
    const authSession: AuthSession = {
      token: nextSession.token,
      refreshToken: nextSession.refreshToken,
      user: nextSession.user,
      createdAt: nextSession.createdAt ?? new Date().toISOString(),
    };
    await persistSession(authSession);
    session.value = authSession;
    return authSession;
  }

  async function completeOAuthCode(code: string): Promise<AuthSession> {
    const { exchangeOAuthCode } = await import("@/services/authApi");
    const nextSession = await exchangeOAuthCode(code);
    return setAuthSession(nextSession);
  }

  async function signInWithGoogle(): Promise<void> {
    await initiateGoogleOAuth();
  }

  async function signInWithNativeGoogle(): Promise<AuthSession> {
    const result = await initiateNativeGoogleSignIn();
    session.value = result;
    return result;
  }

  function getJwtClaims(): ParsedJwtClaims | null {
    if (!session.value) return null;
    return parseJwtClaimsForDisplay(session.value.token);
  }

  return {
    session,
    isLoading,
    user,
    isAuthenticated,
    isAdmin,
    isDeveloper,
    isAiDescribeEnabled,
    isAiPhotosEnabled,
    loadSession,
    ensureSession,
    signOut,
    setAuthSession,
    completeOAuthCode,
    signInWithGoogle,
    signInWithNativeGoogle,
    getJwtClaims,
  };
});
